import type { AxiosError } from "axios";
import axios from "axios";
import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { RadioButton } from "../../../../components/RadioButton/RadioButton";
import {
  H3,
  SmallSectionHeaderRegular,
} from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import {
  RadioButtonContainer,
  Flex,
  Flex1,
  Flex2,
} from "../../../../layout/FormLayout";
import type {
  PIMProductSubscriberPaginatedOutput,
  PIMProductSubscriber,
} from "../../../../types/types";
import type { PIMProduct } from "../../../../types/types.PIM";
import {
  useStoreState,
  TablePlaceholder,
  toTitleCase,
} from "../../../../util/util";
import { SellerAdminTeamsListByProduct } from "../../../admin/SellerAdmin/SellerAdminTeams/SellerAdminTeamsByProduct";
import type { ProductMutate } from "../ProductDetailPage";
import { CustomersList } from "./CustomersList";
import { InfoBlockSmall } from "../../../../components/InfoBlocks/InfoBlocks";
import {
  EditButton,
  PrimaryButtonWithPlusIcon,
  ButtonWithConfirmDialog,
  DeleteButton,
} from "../../../../components/Buttons/Buttons";
import { Row } from "../../../../components/Layout/Layout";
import { ProductOwnerSelector } from "./ProductOwnerSelector";
import useSWR from "swr";
import { SearchBar } from "../../../../components/SearchBar/SearchBar";
import { Table } from "../../../../components/Table/Table";
import { useDebounce } from "../../../../util/hooks";
import { ProductSubscribersSelector } from "./ProductSubscribersSelector";

const AccessHomePageWrapper = styled.div`
  margin-top: 60px;
`;

const HeaderSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;

  & > ${Row} {
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
`;

const RadioSection = styled(RadioButtonContainer)`
  margin-left: -5px;
`;

const SectionDivider = styled.div`
  margin-top: 40px;
  margin-bottom: 20px;
`;

const SubscriberListWrapper = styled.div`
  margin-top: 20px;
`;

type SubscriberRow = {
  id: string;
  name: string;
  email: string;
};

export const AccessHomePage = ({
  product,
  mutateProduct,
}: {
  product: PIMProduct;
  mutateProduct: ProductMutate;
}) => {
  const {
    id: productId,
    is_accessible_by_all_customers_and_distributors: all_customers,
  } = product;

  const [accessMode, setAccessMode] = useState<
    "all_customers" | "selected_customers" | undefined
  >(
    all_customers === true
      ? "all_customers"
      : all_customers === false
      ? "selected_customers"
      : undefined
  );
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [accessConfirmDialogMessage, setAccessConfirmDialogMessage] =
    useState<string>("");
  const [transientAccessMode, setTransientAccessMode] =
    useState<"all_customers" | "selected_customers">();
  const [showOwnerSelector, setShowOwnerSelector] = useState(false);

  const [showSubscribersSelector, setShowSubscribersSelector] = useState(false);
  const [subscriberSearchQuery, setSubscriberSearchQuery] = useState("");
  const [debouncedSubscriberSearchQuery] = useDebounce(
    subscriberSearchQuery,
    1000
  );

  const { tenant_id, storefront_id } = useStoreState();
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();

  const productOwner = product?.product_owner;
  const ownerName = productOwner
    ? `${productOwner.firstname} ${productOwner.lastname}`
    : t("Not Assigned");

  const {
    data: subscribersData,
    error: subscribersError,
    mutate: mutateSubscribersList,
  } = useSWR<PIMProductSubscriberPaginatedOutput, AxiosError>([
    endpoints.v2_storefronts_id_pim_products_id_subscribers(
      storefront_id,
      productId
    ),
    useMemo(
      () => ({
        params: {
          q: debouncedSubscriberSearchQuery,
        },
      }),
      [debouncedSubscriberSearchQuery]
    ),
  ]);

  const isLoadingSubscribers = !subscribersData && !subscribersError;

  const handleRemoveSubscriber = useCallback(
    async (subscriberId: string) => {
      try {
        await axios.delete(
          endpoints.v2_storefronts_id_pim_products_id_subscribers_id(
            storefront_id,
            productId,
            subscriberId
          )
        );
        notifySuccess(t("Subscriber removed successfully."));
        await mutateSubscribersList();
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("Could not remove subscriber. Please try again later."),
          { error }
        );
      }
    },
    [
      mutateSubscribersList,
      notifySuccess,
      notifyError,
      productId,
      storefront_id,
      t,
    ]
  );

  const subscriberTableColumns = useMemo(
    () => [
      {
        header: t("Product Subscribers"),
        accessorKey: "name",
      },
      {
        header: t("Email ID"),
        accessorKey: "email",
      },
      {
        header: t("Business Unit"),
        accessorKey: "business_unit",
      },
      {
        header: t("Country"),
        accessorKey: "country",
      },
      {
        header: "",
        id: "actions",
        cell: ({ row: { original } }: { row: { original: SubscriberRow } }) =>
          product.is_editable ? (
            <ButtonWithConfirmDialog
              Button={DeleteButton}
              testid={`remove-subscriber-${original.id}`}
              handleConfirm={() => handleRemoveSubscriber(original.id)}
              confirmMessage={t(
                "Are you sure you want to remove this subscriber?"
              )}
            />
          ) : null,
      },
    ],
    [handleRemoveSubscriber, t, product.is_editable]
  );

  const subscriberTableData: SubscriberRow[] = useMemo(() => {
    return (
      subscribersData?.data?.map((sub: PIMProductSubscriber) => ({
        id: sub.id,
        name: `${toTitleCase(sub.subscriber.firstname)} ${toTitleCase(
          sub.subscriber.lastname
        )}`,
        email: sub.subscriber.email_address,
        business_unit: sub.subscriber.business_unit ?? "--",
        country: sub.subscriber.country ?? "--",
      })) ?? []
    );
  }, [subscribersData]);

  const confirmAccessMode = async (
    mode: "all_customers" | "selected_customers"
  ) => {
    try {
      await axios.patch<PIMProduct>(
        endpoints.v2_tenants_id_products_id_privacy(tenant_id, productId),
        {
          is_accessible_by_all_customers_and_distributors:
            mode === "all_customers",
        }
      );
      await mutateProduct();
      notifySuccess(t("Access updated successfully"));
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not update access control. Please try again later."),
        {
          error,
        }
      );
    }
  };

  return (
    <AccessHomePageWrapper>
      <HeaderSection>
        <H3 style={{ margin: 0 }}>{t("Teams Management")}</H3>
        <SmallSectionHeaderRegular style={{ margin: 0 }}>
          {t("Control accessibility of your product information for each team")}
        </SmallSectionHeaderRegular>
      </HeaderSection>
      <div style={{ marginBottom: "28px" }}>
        <SellerAdminTeamsListByProduct
          product={product}
          mutateProduct={mutateProduct}
        />
      </div>

      <SectionDivider />

      <HeaderSection>
        <H3 style={{ margin: 0 }}>{t("Access Management")}</H3>
        <SmallSectionHeaderRegular style={{ margin: 0 }}>
          {t("Control accessibility of the product information for your teams")}
        </SmallSectionHeaderRegular>
      </HeaderSection>
      <RadioSection>
        <RadioButton
          name={"allCustomers"}
          value="all_customers"
          disabled={product.status === "archived" || !product.is_editable}
          checked={accessMode === "all_customers"}
          optionTitle={t("All Customers and Distributors")}
          handleChange={() => {
            setAccessConfirmDialogMessage(
              t(
                "Are you sure you want to give access to this product information to all customers and distributors?"
              )
            );
            setTransientAccessMode("all_customers");
            setShowAccessDialog(true);
          }}
        />

        <RadioButton
          name={"selectedCustomers"}
          value="selected_customers"
          disabled={product.status === "archived" || !product.is_editable}
          checked={accessMode === "selected_customers"}
          optionTitle={t("Selected Customers and Distributors")}
          handleChange={() => {
            setAccessConfirmDialogMessage(
              t(
                "Are you sure you want to change the access from all customers and distributors to selected customers and distributors?"
              )
            );
            setTransientAccessMode("selected_customers");
            setShowAccessDialog(true);
          }}
        />
      </RadioSection>
      {accessMode === "all_customers" && (
        <H3 style={{ marginTop: "28px" }}>
          {t(
            "All of your customers & distributors have access to this product information"
          )}
        </H3>
      )}
      {accessMode === "selected_customers" && (
        <CustomersList product={product} />
      )}

      <SectionDivider />

      <HeaderSection>
        <Row>
          <H3 style={{ margin: 0 }}>{t("Product Owner")}</H3>
          <EditButton
            data-testid="edit-owner-button"
            onClick={() => setShowOwnerSelector(true)}
            disabled={!product.is_editable}
          />
        </Row>
        <SmallSectionHeaderRegular style={{ margin: 0 }}>
          {t("Manage the product and receive assets related notifications.")}
        </SmallSectionHeaderRegular>
      </HeaderSection>
      <InfoBlockSmall header={t("Product Owner")} content={ownerName} />

      <SectionDivider />

      <HeaderSection>
        <H3 style={{ margin: 0 }}>{t("Product Subscribers")}</H3>
        <SmallSectionHeaderRegular style={{ margin: 0 }}>
          {t(
            "Receive assets related notifications and updates of this product"
          )}
        </SmallSectionHeaderRegular>
      </HeaderSection>
      <Flex style={{ marginBottom: "24px" }}>
        <Flex2>
          <SearchBar
            query={subscriberSearchQuery}
            placeHolder={t("Search by user or email id")}
            handleChange={(e) => setSubscriberSearchQuery(e.target.value)}
            handleClearInput={() => setSubscriberSearchQuery("")}
          />
        </Flex2>
        <Flex1 style={{ textAlign: "right" }}>
          {product.is_editable ? (
            <PrimaryButtonWithPlusIcon
              onClick={() => setShowSubscribersSelector(true)}
              data-alignright
              data-testid="add-subscriber-button"
            >
              {t("Add")}
            </PrimaryButtonWithPlusIcon>
          ) : null}
        </Flex1>
      </Flex>

      <SubscriberListWrapper>
        <Table
          columns={subscriberTableColumns}
          isLoading={isLoadingSubscribers}
          error={subscribersError}
          data={subscriberTableData}
          Placeholder={
            <TablePlaceholder
              message={
                subscribersError
                  ? t("Error loading subscribers.")
                  : t(
                      "No users to show. Please click the add button to add users."
                    )
              }
            />
          }
        />
      </SubscriberListWrapper>

      <ProductOwnerSelector
        isOpen={showOwnerSelector}
        onClose={() => setShowOwnerSelector(false)}
        product={product}
        mutateProduct={mutateProduct}
      />

      <ProductSubscribersSelector
        isOpen={showSubscribersSelector}
        onClose={() => setShowSubscribersSelector(false)}
        product={product}
        mutateSubscribersList={mutateSubscribersList}
      />

      <ConfirmDialog
        show={showAccessDialog}
        closeDialog={() => {
          setShowAccessDialog(false);
          setTransientAccessMode(undefined);
        }}
        confirmMessage={accessConfirmDialogMessage}
        handleConfirm={() => {
          setAccessMode(transientAccessMode!);
          setShowAccessDialog(false);
          confirmAccessMode(transientAccessMode!);
        }}
      />
    </AccessHomePageWrapper>
  );
};
